import React, { useEffect } from 'react';
import { Clock, Trophy, Star, Play, Folder as FolderIcon, Sparkles, Lock, AlertTriangle } from 'lucide-react';
import { useCategories } from '../../../../hooks/useCategories';
import { useHabitStore } from '../../../../stores/habitStore';
import { useFolderStore } from '../../../../stores/folderStore';
import { useTaskStore } from '../../../../stores/taskStore';
import { useRoutineSessionLogStore } from '../../../../stores/routineSessionLogStore';
import { useDayCycle } from '../../../../hooks/useDayCycle';
import { formatPoints } from '../../../../utils/levelCalculation';
import { formatMinutes, formatClock } from '../../../../utils/time';
import { scheduleRoutine, prayerStepWindow, formatMinOfDay } from '../../../../utils/routineSchedule';
import { prayerLabel } from '../../../../utils/prayerBlocks';
import type { Routine } from '../../../../types';

interface RoutinePreviewProps {
  routine: Routine;
  onStart: () => void;
}

export function RoutinePreview({ routine, onStart }: RoutinePreviewProps) {
  const { getCategoryColor, getCategoryName } = useCategories();
  const { getActiveHabits } = useHabitStore();
  const { getFolderById } = useFolderStore();
  const { tasks: tasksByCategory } = useTaskStore();
  const { fetchSessions, getRoutineSessions } = useRoutineSessionLogStore();
  const habits = getActiveHabits();
  const categoryColor = getCategoryColor(routine.category);

  // Load this routine's run history for the History card.
  useEffect(() => {
    fetchSessions(routine.id);
  }, [routine.id, fetchSessions]);
  const recentRuns = getRoutineSessions(routine.id).slice(0, 5);

  const folderTaskCount = (folderId?: string, category?: string): number =>
    (folderId && category ? tasksByCategory[category]?.filter(t => t.folderId === folderId).length : 0) ?? 0;

  // Time-anchoring: if the routine has a prayer step, back-schedule the whole
  // sequence around the prayer's fixed time and show real clock times.
  const todayCycle = useDayCycle(new Date());
  const schedule = scheduleRoutine(routine.steps, todayCycle);
  const timeAware = schedule.timeAware;
  const itemFor = (stepId: string) => schedule.items.find(i => i.stepId === stepId);

  // Calculate estimated start/end time
  const now = new Date();
  const lastEndMin = schedule.items.length ? schedule.items[schedule.items.length - 1].endMin : null;
  const startLabel = timeAware && schedule.recommendedStartMin != null
    ? formatMinOfDay(schedule.recommendedStartMin)
    : now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const endLabel = timeAware && lastEndMin != null
    ? formatMinOfDay(lastEndMin)
    : new Date(now.getTime() + routine.totalDuration * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
        {/* Left Column - Timeline */}
        <div className="space-y-6">
          {timeAware && schedule.recommendedStartMin != null && (
            <div
              className="rounded-lg border p-4"
              style={{ borderColor: `${getCategoryColor('existential')}59`, backgroundColor: `${getCategoryColor('existential')}0d` }}
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" style={{ color: getCategoryColor('existential') }} />
                <span className="text-sm text-muted-foreground">Prayer-anchored — recommended start</span>
              </div>
              <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
                Start at {formatMinOfDay(schedule.recommendedStartMin)}
              </p>
              {schedule.warnings.map((w, i) => (
                <p key={i} className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {w}
                </p>
              ))}
            </div>
          )}

          <div className="bg-card rounded-lg border border-border p-6">
            <h2 className="text-sm font-medium text-muted-foreground mb-4">Sequence</h2>
            <div className="relative">
              {/* Timeline Items — each step draws a connector down to the next, so
                  the line stops at the last dot (no dangling tail). */}
              <div className="space-y-6">
                {routine.steps.map((step, index) => {
                  const connector = index < routine.steps.length - 1 ? (
                    <span className="absolute left-2.5 top-5 -bottom-6 w-px -translate-x-1/2 bg-border" />
                  ) : null;
                  if (step.type === 'habit') {
                    const habit = habits.find(h => h.id === step.habitId);
                    if (!habit) return null;

                    const habitColor = getCategoryColor(habit.category);

                    return (
                      <div key={step.id} className="relative pl-8">
                        {connector}
                        {/* Timeline Dot */}
                        <div 
                          className="absolute left-0 w-5 h-5 rounded-full border-2 border-background"
                          style={{ 
                            backgroundColor: habitColor,
                            top: '0.125rem'
                          }}
                        />

                        <div 
                          className="p-4 rounded-lg"
                          style={{ backgroundColor: `${habitColor}08` }}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="text-sm font-medium text-foreground">
                                  {habit.name}
                                </h3>
                                <span
                                  className="text-xs px-1.5 py-0.5 rounded"
                                  style={{
                                    backgroundColor: `${habitColor}15`,
                                    color: habitColor
                                  }}
                                >
                                  {getCategoryName(habit.category)}
                                </span>
                                {step.slot && (
                                  <span
                                    className="text-xs px-1.5 py-0.5 rounded border"
                                    style={{ borderColor: `${habitColor}40`, color: habitColor }}
                                  >
                                    {step.slot}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-1">
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                  <Clock className="w-3.5 h-3.5" />
                                  <span>{formatMinutes(step.duration)}</span>
                                </div>
                                <div className="flex items-center gap-1 text-xs">
                                  <Star
                                    className="w-3.5 h-3.5"
                                    style={{ color: habitColor }}
                                  />
                                  <span style={{ color: habitColor }}>
                                    {formatPoints(step.duration * 5)} XP
                                  </span>
                                </div>
                                {timeAware && itemFor(step.id) && (
                                  <span className="text-xs text-muted-foreground tabular-nums">
                                    {formatMinOfDay(itemFor(step.id)!.startMin)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (step.type === 'prayer') {
                    const pColor = getCategoryColor('existential');
                    const w = prayerStepWindow(step, todayCycle);
                    const it = itemFor(step.id);
                    return (
                      <div key={step.id} className="relative pl-8">
                        {connector}
                        <div
                          className="absolute left-0 w-5 h-5 rounded-full border-2 border-background"
                          style={{ backgroundColor: pColor, top: '0.125rem' }}
                        />
                        <div className="p-4 rounded-lg" style={{ backgroundColor: `${pColor}0d` }}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Sparkles className="w-3.5 h-3.5" style={{ color: pColor }} />
                            <h3 className="text-sm font-medium text-foreground">
                              {step.prayerKey ? prayerLabel(step.prayerKey) : 'Prayer'}
                            </h3>
                            <span
                              className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded"
                              style={{ backgroundColor: `${pColor}15`, color: pColor }}
                            >
                              <Lock className="w-3 h-3" /> {step.anchor === 'adhan' ? 'by Adhan' : 'by Iqamah'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                            <Clock className="w-3.5 h-3.5" />
                            <span>
                              {it && !it.unavailable
                                ? `${formatMinOfDay(it.startMin)} – ${formatMinOfDay(it.endMin)}`
                                : w
                                  ? `${formatMinOfDay(w.anchorMin)} – ${formatMinOfDay(w.endMin)}`
                                  : 'Unavailable today'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (step.type === 'folder') {
                    const folder = getFolderById(step.folderId ?? '');
                    const fColor = getCategoryColor(folder?.category || routine.category);
                    return (
                      <div key={step.id} className="relative pl-8">
                        {connector}
                        <div
                          className="absolute left-0 w-5 h-5 rounded-full border-2 border-background"
                          style={{ backgroundColor: fColor, top: '0.125rem' }}
                        />
                        <div className="p-4 rounded-lg" style={{ backgroundColor: `${fColor}08` }}>
                          <div className="flex items-center gap-2">
                            <FolderIcon className="w-3.5 h-3.5" style={{ color: fColor }} />
                            <h3 className="text-sm font-medium text-foreground">{folder?.name || 'Folder'}</h3>
                            <span
                              className="text-xs px-1.5 py-0.5 rounded"
                              style={{ backgroundColor: `${fColor}15`, color: fColor }}
                            >
                              {folderTaskCount(step.folderId, folder?.category)} tasks
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Clock className="w-3.5 h-3.5" />
                              <span>{formatMinutes(step.duration)}</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              <Star className="w-3.5 h-3.5" style={{ color: fColor }} />
                              <span style={{ color: fColor }}>{formatPoints(step.duration * 5)} XP</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={step.id} className="relative pl-8">
                        {connector}
                      {/* Timeline Dot */}
                      <div
                        className="absolute left-0 w-5 h-5 rounded-full bg-muted-foreground border-2 border-background"
                        style={{ top: '0.125rem' }}
                      />

                      <div className="p-4 bg-muted rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-sm font-medium text-foreground">Break</h3>
                            <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                              <Clock className="w-3.5 h-3.5" />
                              <span>{formatMinutes(step.duration)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Stats */}
        <div className="space-y-6">
          {/* Stats Card */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h2 className="text-sm font-medium text-muted-foreground mb-4">Overview</h2>
            <div className="space-y-6">
              {/* Time Stats */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{timeAware ? 'Start at' : 'Start Time'}</span>
                  <span className="text-sm font-medium text-foreground">{startLabel}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground" title="Estimated finish time">Finish (EFT)</span>
                  <span className="text-sm font-medium text-foreground">{endLabel}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Duration</span>
                  <span className="text-sm font-medium text-foreground">
                    {formatMinutes(routine.totalDuration)}
                  </span>
                </div>
              </div>

              <div className="border-t border-border" />

              {/* XP Stats */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total XP</span>
                  <div className="flex items-center gap-1">
                    <Star 
                      className="w-4 h-4" 
                      style={{ color: categoryColor }}
                    />
                    <span className="text-sm font-medium text-foreground">
                      {formatPoints(routine.totalXP)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Activities</span>
                  <span className="text-sm font-medium text-foreground">
                    {routine.steps.filter(s => s.type !== 'break').length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Sequences</span>
                  <span className="text-sm font-medium text-foreground">
                    {routine.steps.length}
                  </span>
                </div>
              </div>

              <div className="border-t border-border" />

              {/* Start Button */}
              <button
                onClick={onStart}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                style={{ backgroundColor: categoryColor }}
              >
                <Play className="w-5 h-5" />
                Start Routine
              </button>
            </div>
          </div>

          {/* Completion History */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h2 className="text-sm font-medium text-muted-foreground mb-4">History</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Times Completed</span>
                <div className="flex items-center gap-1">
                  <Trophy 
                    className="w-4 h-4" 
                    style={{ color: categoryColor }}
                  />
                  <span className="text-sm font-medium text-foreground">
                    {routine.completedCount}
                  </span>
                </div>
              </div>

              {routine.lastCompletedAt && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Last Completed</span>
                  <span className="text-sm font-medium text-foreground">
                    {new Date(routine.lastCompletedAt).toLocaleDateString()}
                  </span>
                </div>
              )}

              {recentRuns.length > 0 && (
                <div className="pt-2 border-t border-border">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Recent runs</p>
                  <div className="space-y-2">
                    {recentRuns.map(run => (
                      <div key={run.id} className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">
                          {run.completedAt ? new Date(run.completedAt).toLocaleDateString() : '—'}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className="text-foreground tabular-nums">
                            {formatClock(run.durationSeconds)}
                          </span>
                          <span className="flex items-center gap-1" style={{ color: categoryColor }}>
                            <Star className="w-3 h-3" />
                            {formatPoints(run.xpEarned)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}